import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Separator } from '../../components/ui/separator';
import {
  Wallet,
  ArrowUpRight,
  ArrowDownLeft,
  RefreshCw,
  Send,
  Download,
  TrendingUp,
  TrendingDown,
  Clock,
  User,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { cn } from '../../lib/utils';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

// Transaction type icons and colors
const TRANSACTION_TYPES = {
  credit: { icon: ArrowDownLeft, color: 'text-green-400', bg: 'bg-green-500/20', label: 'Crédito' },
  debit: { icon: ArrowUpRight, color: 'text-red-400', bg: 'bg-red-500/20', label: 'Débito' },
  refund: { icon: RefreshCw, color: 'text-blue-400', bg: 'bg-blue-500/20', label: 'Estorno' },
  transfer_in: { icon: ArrowDownLeft, color: 'text-purple-400', bg: 'bg-purple-500/20', label: 'Recebido' },
  transfer_out: { icon: Send, color: 'text-orange-400', bg: 'bg-orange-500/20', label: 'Enviado' },
};

export const WalletModal = ({
  open,
  onOpenChange,
  tripId,
  userId,
  userName,
  isOwnWallet = false,
  formatCurrency,
}) => {
  const { api } = useAuth();
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (open && tripId && userId) {
      fetchWalletData();
    }
  }, [open, tripId, userId]);

  const fetchWalletData = async () => {
    setLoading(true);
    try {
      const endpoint = isOwnWallet 
        ? `/trips/${tripId}/wallets/me?include_transactions=true`
        : `/trips/${tripId}/wallets/${userId}?include_transactions=true`;
      
      const response = await api.get(endpoint);
      setWallet(response.data);
      setTransactions(response.data.transactions || []);
    } catch (error) {
      console.error('Error fetching wallet:', error);
      toast.error('Erro ao carregar carteira');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = typeof dateStr === 'string' ? parseISO(dateStr) : dateStr;
      return format(date, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
    } catch {
      return dateStr;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90vh] p-0 flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-500 to-orange-600 p-4 text-white shrink-0">
          <DialogHeader>
            <div className="flex items-center gap-2 mb-2">
              <Wallet className="w-5 h-5" />
              <span className="text-sm opacity-90">Carteira</span>
            </div>
            <DialogTitle className="text-xl text-white">
              {userName || 'Membro'}
            </DialogTitle>
          </DialogHeader>
          
          {/* Balance Card */}
          {wallet && (
            <div className="mt-4 bg-white/10 rounded-lg p-4">
              <p className="text-sm opacity-80">Saldo disponível</p>
              <p className="text-3xl font-bold mt-1">
                {formatCurrency(wallet.balance)}
              </p>
              <div className="flex gap-4 mt-3 text-sm">
                <div className="flex items-center gap-1">
                  <TrendingUp className="w-4 h-4 text-green-300" />
                  <span className="opacity-80">Créditos:</span>
                  <span className="font-medium">{formatCurrency(wallet.total_credits)}</span>
                </div>
                <div className="flex items-center gap-1">
                  <TrendingDown className="w-4 h-4 text-red-300" />
                  <span className="opacity-80">Débitos:</span>
                  <span className="font-medium">{formatCurrency(wallet.total_debits)}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Transactions */}
        <div className="flex-1 overflow-y-auto p-4">
          <h3 className="text-sm font-medium text-slate-400 mb-3 flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Extrato
          </h3>
          
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin text-orange-400" />
            </div>
          ) : transactions.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Wallet className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Nenhuma transação ainda</p>
            </div>
          ) : (
            <div className="space-y-2">
              {transactions.map((tx) => {
                const txType = TRANSACTION_TYPES[tx.type] || TRANSACTION_TYPES.credit;
                const Icon = txType.icon;
                const isPositive = ['credit', 'refund', 'transfer_in'].includes(tx.type);
                
                return (
                  <div 
                    key={tx.id}
                    className="flex items-start gap-3 p-3 bg-slate-800/50 rounded-lg"
                  >
                    <div className={cn('p-2 rounded-full', txType.bg)}>
                      <Icon className={cn('w-4 h-4', txType.color)} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white truncate">{tx.description}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="outline" className={cn('text-xs border-0', txType.bg, txType.color)}>
                          {txType.label}
                        </Badge>
                        <span className="text-xs text-slate-500">
                          {formatDate(tx.created_at)}
                        </span>
                      </div>
                      {tx.related_user_name && (
                        <div className="flex items-center gap-1 mt-1 text-xs text-slate-400">
                          <User className="w-3 h-3" />
                          {tx.related_user_name}
                        </div>
                      )}
                    </div>
                    <div className={cn(
                      'text-sm font-medium',
                      isPositive ? 'text-green-400' : 'text-red-400'
                    )}>
                      {isPositive ? '+' : '-'}{formatCurrency(tx.amount)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Transfer Modal
export const WalletTransferModal = ({
  open,
  onOpenChange,
  tripId,
  members = [],
  currentUserId,
  formatCurrency,
  onTransferComplete,
}) => {
  const { api } = useAuth();
  const [fromUserId, setFromUserId] = useState(currentUserId);
  const [toUserId, setToUserId] = useState('');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [wallets, setWallets] = useState([]);

  useEffect(() => {
    if (open && tripId) {
      fetchWallets();
    }
  }, [open, tripId]);

  const fetchWallets = async () => {
    try {
      const response = await api.get(`/trips/${tripId}/wallets`);
      setWallets(response.data);
    } catch (error) {
      console.error('Error fetching wallets:', error);
    }
  };

  const handleTransfer = async () => {
    if (!fromUserId || !toUserId || !amount || parseFloat(amount) <= 0) {
      toast.error('Preencha todos os campos');
      return;
    }

    if (fromUserId === toUserId) {
      toast.error('Selecione membros diferentes');
      return;
    }

    setLoading(true);
    try {
      await api.post(`/trips/${tripId}/wallets/transfer`, {
        from_user_id: fromUserId,
        to_user_id: toUserId,
        amount: parseFloat(amount),
        description: description || 'Transferência entre membros',
      });
      
      toast.success('Transferência realizada com sucesso!');
      onOpenChange(false);
      onTransferComplete?.();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao realizar transferência';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const senderWallet = wallets.find(w => w.user_id === fromUserId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="w-5 h-5 text-orange-400" />
            Transferir Saldo
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 pt-2">
          {/* From */}
          <div className="space-y-2">
            <Label>De:</Label>
            <select
              value={fromUserId}
              onChange={(e) => setFromUserId(e.target.value)}
              className="w-full p-2 rounded-md bg-slate-800 border border-slate-700 text-white"
            >
              {wallets.map(w => (
                <option key={w.user_id} value={w.user_id}>
                  {w.user_name} ({formatCurrency(w.balance)})
                </option>
              ))}
            </select>
            {senderWallet && (
              <p className="text-xs text-slate-400">
                Saldo disponível: {formatCurrency(senderWallet.balance)}
              </p>
            )}
          </div>

          {/* To */}
          <div className="space-y-2">
            <Label>Para:</Label>
            <select
              value={toUserId}
              onChange={(e) => setToUserId(e.target.value)}
              className="w-full p-2 rounded-md bg-slate-800 border border-slate-700 text-white"
            >
              <option value="">Selecione...</option>
              {wallets.filter(w => w.user_id !== fromUserId).map(w => (
                <option key={w.user_id} value={w.user_id}>
                  {w.user_name}
                </option>
              ))}
            </select>
          </div>

          {/* Amount */}
          <div className="space-y-2">
            <Label>Valor:</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0,00"
              className="text-lg"
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label>Descrição (opcional):</Label>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Ex: Acerto de contas"
            />
          </div>

          <Separator />

          <Button 
            onClick={handleTransfer} 
            disabled={loading}
            className="w-full bg-orange-500 hover:bg-orange-600"
          >
            {loading ? (
              <RefreshCw className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Send className="w-4 h-4 mr-2" />
            )}
            Transferir
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
